import { DataService } from './service/data.service';
import { AppConfigService } from 'src/app/config/config.service';
import { BusinessAssociation, LenderConfiguration } from '@shared/model/lender-config.model';
import { DOCUMENT, PlatformLocation } from '@angular/common';
import { AfterViewChecked, AfterViewInit, Component, HostBinding, HostListener, Inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router, ActivationStart, ActivatedRoute, Params } from '@angular/router';
import {
  AppInsightsService,
  DictionaryService,
  ThemeService,
  TokenService,
  ToastService,
  AppConfigService as ConfigService
} from '@cudirect/shared-ui-library';
import { fromEvent, merge, of, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { LegalService } from 'src/legal/legal.service';
import { RoutingUrl } from 'src/shared/constant/route-const';
import { UserSessionService } from 'src/shared/service/user-session.service';
import { ChatIntegrationService } from '@shared/service/chat-integration.service';
import * as FullStory from '@fullstory/browser';
import { AppConfig } from './config/config.type';
import pkg from 'package.json';
import { ContactType } from '@cudirect/shared-ui-library/lib/models/contact';
import { TimerService } from '@shared/service/timer.service';
import { VersioningService } from './service/versioning.service';
import { StatefulService } from './service/stateful.service';
import { IframeService } from './service/iframe.service';
import { SubscriptionEmitService } from '@workflow/ecp-shared/service/subscription-emit/subscription-emit.service';
import { UserService } from '@workflow/ecp-shared/service/user/user.service';
import { NotificationConstant } from '@workflow/ecp-shared/constant/notification.const';
import { WorkflowService } from '@workflow/service/workflow/workflow.service';
import { LenderService } from '@shared/service/lender/lender.service';
import { datadogRum } from '@datadog/browser-rum';
import { PageInstance } from '@workflow/class/page.service';

/**
 * Component representing app component section
 * @Component
 */
@Component({
  selector: 'cu-root',
  styleUrls: ['./app.component.scss'],
  templateUrl: './app.component.html'
})
/**
 *
 * Class representing app component section
 * @export
 * @class AppComponent
 * @implements {AfterViewChecked}
 * @implements {OnInit}
 */
export class AppComponent implements OnInit, AfterViewChecked, AfterViewInit, OnDestroy {
  /** Workflow page */
  public page: PageInstance;
  /** Return ready status */
  public ready = false;
  /** Return title name */
  public title = 'MyOri';
  /** Lender Data coming from local lenderConfig */
  public lenderConfig: LenderConfiguration;
  /** Client context for chat widget */
  /** comment  for chat widget */
  // public chatWidgetContext: any;
  public showUserSession = false;
  // Active Business Contact Associations
  public activeBusinessAssociations: BusinessAssociation[] = [];

  /** Version of ECP.WEB / arcDX / DOP - Leveraged for Cache Busting */
  public version = pkg.version;

  public userAuthenticated = false;
  private subscriptions: Subscription[] = [];

  @HostBinding('class.bl-iframe')
  public frame = false;

  // Is this the Admin Portal?
  public isAdmin = false;

  // Hide Footer
  public hideFooter = false;

  /**
   * For PWA Manifest file
   */
  public siteUrl = window.location.origin;

  /**
   * Get the app config data from config.json
   */
  public appConfigData: AppConfig;
  private elRef: HTMLBodyElement;
  private networkStatus$: Subscription = Subscription.EMPTY;

  /**
   * True when resume banner should be shown;
   */
  public showResumeBanner = false;
  private dictionarySub: Subscription;
  public showContactUsPanel = false;

  constructor(
    public dictionary: DictionaryService,
    public legalService: LegalService,
    public lenderService: LenderService,
    public themeService: ThemeService,
    private readonly toast: ToastService,
    private readonly location: PlatformLocation,
    private readonly router: Router,
    private readonly subscribeEmitService: SubscriptionEmitService,
    private readonly activeRoute: ActivatedRoute,
    private readonly workflowService: WorkflowService,
    private readonly userService: UserService,
    private readonly userSessionService: UserSessionService,
    private readonly titleService: Title,
    private readonly chatIntegrationService: ChatIntegrationService,
    private readonly appInsightsService: AppInsightsService,
    private readonly appConfigService: AppConfigService,
    private configService: ConfigService,
    @Inject(DOCUMENT) public document: Document,
    private dataService: DataService,
    private tokenService: TokenService,
    private timerService: TimerService,
    private versioningService: VersioningService,
    private statefulService: StatefulService,
    private iframeService: IframeService,
    private renderer: Renderer2
  ) {
    this.tokenService.configureCache(configService?.settings?.arcDXTokenStorageType || 'sessionStorage', 'ecp-user-session');
    this.elRef = this.document.getElementsByTagName('body')[0];
    dictionary.path = '/assets/dictionary'; // relative or absolute path
    dictionary.file = 'dictionary'; // all component and language flavors merge to here

    // Define Dictionary Overrides at the App and Lender level
    const lenderOverrides = this.lenderService.lenderData?.dictionaryOverride ? this.lenderService.lenderData?.dictionaryOverride : null;
    let appOverrides = {
      'ea-authenticate-create': {
        errors: {
          failedDeferredCreate: {
            detail: 'Please make sure your information matches what was entered on your application'
          }
        }
      }
    };

    // If lender override strings are present, lets add them
    if (typeof lenderOverrides === 'string') {
      const jsonOverrides = JSON.parse(lenderOverrides);
      // Merge lender with App Overrides
      appOverrides = { ...appOverrides, ...jsonOverrides };
    }
    // Override strings in the dictionary
    dictionary.jsonOverride = appOverrides;

    // Init dictionary service w/ undefined (path/file defined above) and a version for cachebusting
    dictionary.load(undefined, Date.now().toString());
    this.dictionarySub = dictionary.onLoad.subscribe(() => {
      this.dictionarySub.unsubscribe();
      this.dataService.dictionaryIsReady$.next(true);
    });

    // THEME SERVICE CONFIGURATION OPTIONS
    // Override theme service defaults
    this.themeService.defaultLogo = '';
    this.themeService.testFallbackLogo = false;
    this.themeService.enableLocalStorageTesting = false;
    this.themeService.enablePWAMode = true;
    this.themeService.manifestValues = {
      scope: this.siteUrl + '/',
      start_url: this.siteUrl + '/',
      name: this.lenderService?.lenderData?.lenderName,
      short_name: this.lenderService?.lenderData?.lenderName,
      description: 'Borrower Portal for ' + this.lenderService?.lenderData?.lenderName,
      icon192: 'assets/images/pwa-icons/icon-192.png',
      icon512: 'assets/images/pwa-icons/icon-512.png'
    };
  }

  @HostListener('window:popstate', ['$event'])
  public onPopState(): void {
    // Handle popstate event here
    this.showUserSession = false;
  }

  // TODO: This is a bad way to set the title, it should be pulled from meta data from the route object
  public pageTitle(): string {
    const dictionary = this.dictionary;
    const dict = (value: string): string => dictionary.read('pageTitles/' + value);

    // Admin Portal
    if (this.isPath('admin/')) {
      return dict('adminPortal');
    }
    // User page titles
    if (this.isPath('user/login')) {
      return dict('userLogin');
    }
    if (this.isPath('user/registration', 'user/light-registration')) {
      return dict('userRegistration');
    }
    if (this.isPath('user/profile')) {
      return dict('userProfile');
    }
    // Post application titles
    if (this.isPath('/dashboard')) {
      return dict('dashboard');
    }
    if (this.isPath('postapp/overview')) {
      return dict('portalOverview');
    }
    if (this.isPath('postapp/tasks')) {
      return dict('portalDocuments');
    }
    if (this.isPath('postapp/document')) {
      return dict('portalDocuments');
    }
    if (this.isPath('postapp/activity')) {
      return dict('portalActivity');
    }
    // Legal pages titles
    if (this.isPath('legal/terms-of-use')) {
      return dict('legalTermsOfUse');
    }
    if (this.isPath('legal/privacy-policy')) {
      return dict('legalPrivacyPolicy');
    }
    if (this.isPath('terms-and-policy')) {
      return dict('origenceTermsPolicy');
    }
    // Workflow application pages (default using page h1 tag)
    if (this.page && this.page.customTitleOrDefault) {
      return this.page.customTitleOrDefault;
    }

    // Global error/contact us page
    if (this.isPath('/oops')) {
      return dict('contactUs');
    }
  }

  private checkNetworkStatus() {
    this.networkStatus$ = merge(of(null), fromEvent(window, 'online'), fromEvent(window, 'offline'))
      .pipe(map(() => navigator.onLine))
      .subscribe((status) => {
        if (status) {
          if (this.appConfigService.getNetworkStatus() === false) {
            this.showToaster('You are back online...', 2000);
          }
        } else {
          this.showToaster('You are offline. Please reconnect...', 60000);
        }
        this.appConfigService.setNetworkStatus(status);
      });
  }

  /**
   * Show toaster message by invoking share library
   * @param text
   */
  private showToaster(text: string, timeout: number = 5000) {
    this.toast.open(this.toastHtml(text), timeout, `<span></span>`, 'burnt-toast').subscribe(() => {});
  }

  /**
   * Toaster text
   * @param text
   * @returns
   */
  private toastHtml(text: string): string {
    const html = `<span class="bl-font-weight--800 bl-font-color--white">${text}</span> `;
    return html;
  }

  /**
   * Since ng14, this is provided by the router, but we're not using it yet
   * https://v14.angular.io/guide/router#setting-the-page-title
   */
  private setPageTitle(): void {
    // Adds null check for lender name, as during implementation, we may not have a lender name set
    // Will remove a lot of unnecessary errors in the console in UAT/PROD
    const lenderName = this.lenderConfig?.lenderName ? this.lenderConfig.lenderName : 'No Lender Name Set';
    const spacer = ' - ';
    const pageTitle = this.pageTitle() ? this.pageTitle() + spacer + lenderName : lenderName;
    this.titleService.setTitle(pageTitle);
  }

  // Pass in 1 or more url path strings to check if
  // current location contains provided string(s)
  public isPath(...pathnames: string[]): boolean {
    const currentPath = this.location.pathname;
    return currentPath && pathnames.some((pathname) => currentPath.toLowerCase().includes(pathname.toLowerCase()));
  }

  /**
   * Determines if were in Admin Portal or not by path
   * @returns boolean
   */
  public adminPages(): boolean {
    this.isAdmin = this.isPath('/admin');
    return this.isAdmin;
  }

  /**
   * Hide Footer & ReCaptcha for Admin Pages & Esign iframe
   */
  public setHideFooterFlag(): void {
    this.hideFooter = this.isAdmin || this.isPath('/document-esign');
  }

  /** property to hide header for esign call back pages */
  public get hideHeader(): boolean {
    return (
      this.frame || (!this.router.url.includes('/integration/instatouch') && this.router.url.includes('/integration/')) || this.adminPages()
    );
  }

  // TECH: This should only be true on 1 condition, when the user is authenticated or not.
  // We dont need this function to evaluate on path
  public showHeaderUserDropdown(): boolean {
    return !this.isPath('user/login', 'user/registration', 'user/light-registration') && this.userService.isExistingUser;
  }

  private startSessionOnInit(): void {
    this.userSessionService.startTimer();
  }

  // NOTE: would like to move this to postapp/components/document-esign.component.ts
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private processESignResponse(response: any): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.subscribeEmitService.emit<any>(NotificationConstant.ESign.ESign_Subscription, response);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private processLocalStorageResponse(response: any): void {
    if (!response) {
      return;
    }
    // do what you want on logout event
    if (
      response.key === NotificationConstant.LocalStorage.LogOut_Event &&
      this.router.url !== RoutingUrl.Login &&
      !this.router.url.includes('/admin')
    ) {
      this.router.navigate([RoutingUrl.Login]);
    }
  }

  /**
   *
   * Initilize while page load
   * @memberof AppComponent
   */
  public ngOnInit(): void {
    // Special application behavior is required if the app is being iframed
    const appIsIframed = window.self !== window.top;
    appIsIframed && this.iframeService.init();

    this.checkNetworkStatus();
    // Grab the data from config.json
    this.appConfigData = this.appConfigService.getData();
    // Fire up Datadog here
    this.initDatadogRUM();
    // Init application insights
    // Update this to use open telemetry specs
    this.initAppInsights();
    this.setFrameFlag();

    this.subscriptions.push(
      this.activeRoute.queryParams.subscribe((params) => {
        this.storeParamtoSession(params);
      })
    );

    // Set workflow page
    this.page = this.workflowService.currentPage;

    // Set Lender Data (API)
    this.lenderConfig = this.lenderService.lenderDataInfo;
    this.activeBusinessAssociations = this.lenderConfig?.businessAssociations?.filter((f) => f?.enabled === true);
    // Initialize chat provider (or none) - no FF here because it is comes from LC
    // NOTE: We are aware that lenders who iframe into hb sites will *not* have chat - the hb site should manage its own instance of chat
    // NOTE: this.router.routerState.snapshot.url is not behaving at init time & PlatformLocation is not supposed to be used (above)
    // TODO: Down the road, we may need to implement logic to add/remove the snippet on route change - currently, it's rudiementary (onload)
    // TODO: Down the road, we may want to move the route ignore logic into the service, and make it more elegant (pass routes to method)
    !this.adminPages() && !appIsIframed && this.chatIntegrationService.initialzeChatProvider(this.lenderConfig?.chatIntegration);

    this.setHideFooterFlag();

    this.router.events.subscribe((event) => {
      this.setPageFocus(event);
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.subscribeEmitService.register<any>(NotificationConstant.Asset.Finicity_Subscription);

    // NOTE: would like to move this to postapp/components/document-esign.component.ts
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.subscribeEmitService.register<any>(NotificationConstant.ESign.ESign_Subscription);

    this.subscriptions.push(
      this.userSessionService.timerEvent.subscribe((action) => {
        this.handleTimerEvent(action);
      })
    );

    // Idile popup clock
    if (!this.isPath('/admin')) {
      this.startSessionOnInit();
    }

    // TODO: Why are we checking this here?
    this.checkUsertoken();

    // Moved to bottom of ngOnInit, as Initializing full story should not be blocking the page load
    // Can we wrap with in a method, and only call it if appConfigData.fullStory.deactivate === 'false'?
    // Seems like a waste to initate FS if we're not using it.
    if (this.appConfigData.fullStory.deactivate !== 'true') {
      FullStory.init({
        orgId: this.appConfigData.fullStory.orgId,
        devMode: this.appConfigData.fullStory.deactivate === 'true'
      });
    }

    // Check for version updates periodically
    this.versioningService.startVersionCheck();

    // Capture app init duration in AppInsights
    this.timerService.endTimerAndMeasure('app-init-oninit', 'ecp.ui.app-init-duration');
  }

  /**
   * Check the url has frame to set flag
   * @returns true / false
   * @remarks
   * TODO: Consolidate this with the iframe service
   */
  private setFrameFlag(): void {
    // First check if 'iframe' is in session storage, if not, set to false
    this.frame = sessionStorage.getItem('iframe') ? !sessionStorage.getItem('iframe') : false;

    // Grab the query string, if present
    const location = this.location.search?.toLowerCase();
    const isIframe = location.includes('frame=yes') || location.includes('frame=true');

    // If its present in the query string, use that, else persist the value from above.
    this.frame = this.frame || isIframe;

    // Save to session storage
    sessionStorage.setItem('iframe', isIframe.toString());
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isValidContactType(type: any): type is ContactType {
    return ['phone', 'sms', 'email', 'url'].includes(type.toLowerCase());
  }

  // Might need to moved to a shared service, utilities file
  public generateContactUrl(contactType: ContactType, contactValue: string): string {
    // contactType = contactType as ContactType;
    let urlPrefix = '';

    if (contactType) {
      switch (contactType.toLowerCase()) {
        case 'phone':
        case 'sms':
          urlPrefix = 'tel:';
          break;
        case 'email':
          urlPrefix = 'mailto:';
          break;
      }
    }
    return urlPrefix + contactValue;
  }

  /**
   * Sets the page focus to the header on page load, per ADA/WCA requirements
   * @param event - Event
   */
  private setPageFocus(event): void {
    if (event instanceof ActivationStart) {
      const element = this.document.getElementById('global-header');
      if (element && !event.snapshot.fragment) {
        element.scrollIntoView();
      }
    }
  }

  /**
   * Function to check if the user token is valid
   */
  private checkUsertoken(): void {
    if (this.userService.UserInfo?.token) {
      const token = this.userService.UserInfo.token;
      const auth = this.statefulService.wrap('ss:authorization').valueString;
      const ola_auth = this.statefulService.wrap('ss:auth-ola').valueString;
      if (token !== auth && token !== ola_auth && token && !this.isPath('/legal', '/admin', '/integration')) {
        this.userSessionService.logOut(true);
      }
    }
  }

  /**
   * Function to handle timer event, pulled out of ngOnInit for readability, SQ code smell
   * @param action - string
   */
  private handleTimerEvent(action: string): void {
    switch (action) {
      case 'show':
        if (this.router.routerState.snapshot.url.includes('/admin')) {
          // User is in AP, not ECP. Don't show modal, just log them out.
          this.userSessionService.logOut(false);
        } else {
          this.showUserSession = true;
        }
        break;
      case 'logout':
        this.userSessionService.logOut(true, true);
        this.showUserSession = false;
        break;
      case 'hide':
        this.showUserSession = false;
        break;
      default:
    }
  }

  /** Initialize Application Insights - Child libs / widgets should be pulling from the same instance */
  private initAppInsights(): void {
    const aiInstrumentationKey = this.appConfigData?.aiInstrumentationKey;
    this.appInsightsService.initAppInsights(aiInstrumentationKey);

    const telemetryInitializer = (envelope) => {
      // TODO: Check to see if we still need tenantId here
      envelope.data.tenantId = this.lenderService.lenderDataInfo.tenantId;
      envelope.data.lenderCode = this.lenderService.lenderDataInfo.consumerLoanCode;
    };
    this.appInsightsService.appInsights.addTelemetryInitializer(telemetryInitializer);
  }

  /**
   *
   * Initilize after page load
   * @memberof AppComponent
   */
  public ngAfterViewInit(): void {
    // E-Sign Integration
    // TODO: do we need this at a global level?
    // NOTE: would like to move this to postapp/components/document-esign.component.ts
    window.addEventListener(NotificationConstant.ESign.ESign_Event, this.processESignResponse.bind(this));

    // listen to storage event
    // TODO: Do we need this at all, and a global level? What are we listening for?
    window.addEventListener(NotificationConstant.LocalStorage.Window_Event_Name, this.processLocalStorageResponse.bind(this));

    // Mesaure afterview timing to diff against oninit timing - we want this to come as late as possible in the app load lifecycle
    this.timerService.endTimerAndMeasure('app-init-afterview', 'ecp.ui.app-viewload-duration');

    if (!this.lenderService.lenderData?.enableRecaptcha || this.hideFooter) {
      this.renderer.addClass(this.document.body, 'recaptcha-disabled');
    }
  }

  /**
   *
   * Initilize while after view rendered
   * @memberof AppComponent
   */
  public ngAfterViewChecked(): void {
    if (this.page?.id !== this.workflowService?.currentPage?.id) {
      this.page = this.workflowService.currentPage;
    }
    // Set page title
    this.setPageTitle();
  }

  private storeParamtoSession(queryParams: Params) {
    if (!this.userService.isAuthenticated) {
      const params = [
        'autoredirect',
        'oidcid',
        'loantype',
        'loantypeoption',
        'target',
        'otutoken',
        'continue',
        'promocode',
        'MembershipType',
        'IsCUEmployee'
      ];
      // Fix to case sensitive issue with reading query params.
      const queryKeys = Object.keys(queryParams);
      params.forEach((p) => {
        const queryParamKey = queryKeys.find((t) => t.toLowerCase() === p.toLowerCase());
        if (queryParamKey && queryParams?.[queryParamKey]) {
          sessionStorage.setItem(p, queryParams[queryParamKey]);
        }
      });
    }
  }

  public ngOnDestroy(): void {
    this.networkStatus$.unsubscribe();
    window.removeEventListener(NotificationConstant.LocalStorage.Window_Event_Name, this.processLocalStorageResponse.bind(this));

    this.subscribeEmitService.unRegister(NotificationConstant.Asset.Finicity_Subscription);
    // NOTE: would like to move this to postapp/components/document-esign.component.ts
    this.subscribeEmitService.unRegister(NotificationConstant.ESign.ESign_Subscription);
    this.subscriptions.forEach((subscription: Subscription) => {
      subscription.unsubscribe();
    });
    this.subscriptions = [];
  }

  /**
   * Called when user clicks continue on session warning modal. The click will reset the timer automatically.
   */
  public continueSession(): void {
    this.showUserSession = false;
  }

  /**
   * Called when user clicks logout on session warning modal or user times out. Logout is handled from the session modal component.
   * This just closes modal.
   */
  public handleLogoutClick(): void {
    this.showUserSession = false;
  }

  /**
   * Initialize Datadog RUM
   * @remarks This is a global init for the entire app
   * @remarks This should only be called once, and only if we want to enable it
   * @remarks This should be called after the app has been initialized
   * @requires - Values from AppConfigService, which pull in from config.prod.json
   * @returns void
   */
  public initDatadogRUM(): void {
    // Regex patterns to identify known bot instances:
    // https://docs.datadoghq.com/real_user_monitoring/guide/identify-bots-in-the-ui/#filter-out-bot-sessions-on-intake
    // eslint-disable-next-line prettier/prettier, max-len
    const botPattern =
      // eslint-disable-next-line max-len
      '(googlebot/|bot|Googlebot-Mobile|Googlebot-Image|Google favicon|Mediapartners-Google|bingbot|slurp|java|wget|curl|Commons-HttpClient|Python-urllib|libwww|httpunit|nutch|phpcrawl|msnbot|jyxobot|FAST-WebCrawler|FAST Enterprise Crawler|biglotron|teoma|convera|seekbot|gigablast|exabot|ngbot|ia_archiver|GingerCrawler|webmon |httrack|webcrawler|grub.org|UsineNouvelleCrawler|antibot|netresearchserver|speedy|fluffy|bibnum.bnf|findlink|msrbot|panscient|yacybot|AISearchBot|IOI|ips-agent|tagoobot|MJ12bot|dotbot|woriobot|yanga|buzzbot|mlbot|yandexbot|purebot|Linguee Bot|Voyager|CyberPatrol|voilabot|baiduspider|citeseerxbot|spbot|twengabot|postrank|turnitinbot|scribdbot|page2rss|sitebot|linkdex|Adidxbot|blekkobot|ezooms|dotbot|Mail.RU_Bot|discobot|heritrix|findthatfile|europarchive.org|NerdByNature.Bot|sistrix crawler|ahrefsbot|Aboundex|domaincrawler|wbsearchbot|summify|ccbot|edisterbot|seznambot|ec2linkfinder|gslfbot|aihitbot|intelium_bot|facebookexternalhit|yeti|RetrevoPageAnalyzer|lb-spider|sogou|lssbot|careerbot|wotbox|wocbot|ichiro|DuckDuckBot|lssrocketcrawler|drupact|webcompanycrawler|acoonbot|openindexspider|gnam gnam spider|web-archive-net.com.bot|backlinkcrawler|coccoc|integromedb|content crawler spider|toplistbot|seokicks-robot|it2media-domain-crawler|ip-web-crawler.com|siteexplorer.info|elisabot|proximic|changedetection|blexbot|arabot|WeSEE:Search|niki-bot|CrystalSemanticsBot|rogerbot|360Spider|psbot|InterfaxScanBot|Lipperhey SEO Service|CC Metadata Scaper|g00g1e.net|GrapeshotCrawler|urlappendbot|brainobot|fr-crawler|binlar|SimpleCrawler|Livelapbot|Twitterbot|cXensebot|smtbot|bnf.fr_bot|A6-Indexer|ADmantX|Facebot|Twitterbot|OrangeBot|memorybot|AdvBot|MegaIndex|SemanticScholarBot|ltx71|nerdybot|xovibot|BUbiNG|Qwantify|archive.org_bot|Applebot|TweetmemeBot|crawler4j|findxbot|SemrushBot|yoozBot|lipperhey|y!j-asr|Domain Re-Animator Bot|AddThis)';

    const regex = new RegExp(botPattern, 'i');
    const agent = navigator.userAgent;
    const isPlaywrightDomain = location.href.includes('test-automation');

    // define var conditionalSampleRate as 0 if the userAgent matches a pattern in botPatterns
    // otherwise, define conditionalSampleRate as 100
    // const conditionalSampleRate = regex.test(navigator.userAgent) ? 0 : 100;

    // Only initialize if we explicitly want it to be enabled
    // We want to have this off by default in local development
    // And in playwright testing as to avoid running up our costs
    // Need to JSON.parse to this to coerce to boolean
    // Dont enable this if its a bot or a test-automation run
    if (JSON.parse(this.appConfigData?.dataDog.enabled) === true && !regex.test(agent) && !isPlaywrightDomain) {
      datadogRum.init({
        applicationId: this.appConfigData?.dataDog.applicationId,
        clientToken: this.appConfigData?.dataDog.clientToken,
        // `site` refers to the Datadog site parameter of your organization
        // see https://docs.datadoghq.com/getting_started/site/
        site: 'us3.datadoghq.com',
        service: 'arc-dx',
        env: this.appConfigData?.dataDog.environment,
        // Specify a version number to identify the deployed version of your application in Datadog
        version: this.version,
        sessionSampleRate: JSON.parse(this.appConfigData?.dataDog.sessionSampleRate),
        sessionReplaySampleRate: JSON.parse(this.appConfigData?.dataDog.sessionReplaySampleRate),
        trackUserInteractions: this.appConfigData?.dataDog.trackUserInteractions,
        trackResources: this.appConfigData?.dataDog.trackResources,
        trackLongTasks: this.appConfigData?.dataDog.trackLongTasks,
        defaultPrivacyLevel: this.appConfigData?.dataDog.defaultPrivacyLevel,
        // Need to add a list of allowed tracing urls to capture full traces
        // Keeping this hardcoded here for now and can consider moving to the config file if
        // We may nneed to make this configirable, will revisit if needed
        allowedTracingUrls: [/https:\/\/.*\.cudirect\.com/, /https:\/\/.*\.origence\.com/],
        // https://docs.datadoghq.com/real_user_monitoring/browser/advanced_configuration/?tab=npm
        beforeSend: (event, context) => {
          // This might be availiable elsewhere, but for now, we're pulling it from session storage
          // Might want to revist and include more context for the setUser object
          const appNumber = sessionStorage.getItem('appNumber');

          // If appNumber is set, we want to set the user id to the appNumber
          // This will allow us to track the user in Datadog
          if (appNumber && appNumber !== '-1') {
            // collect a RUM resource's response headers
            if (event.type === 'resource' && event.resource.type === 'fetch') {
              // Add user meta data to the RUM event
              // We might extend this to add additional user attributes in the future
              datadogRum.setUser({
                id: appNumber,
                orgCode: this.lenderService.lenderDataInfo.consumerLoanCode,
                lenderName: this.lenderService.lenderDataInfo.lenderName
              });
            }
          }
          return true;
        }
      });
    }
  }
}
